import ReactFlow, {
    Background,
    BackgroundVariant,
    ControlButton,
    Controls,
    MiniMap,
    Position,
    useStore,
} from 'reactflow';
import MapIcon from '@mui/icons-material/Map';
import { useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { nodesCanBeMovedByKeyboardSelector } from '@pages/schemes/utils';
import useIsTakingSchemeScreenshot from '@pages/schemes/hooks/useIsTakingSchemeScreenshot';
import useDeleteNode from '@pages/schemes/hooks/useDeleteNode';

const FlowInstance = ({
    nodes,
    edges,
    defaultViewport,
    defaultEdgeOptions,
    proOptions,
    nodeTypes,
    edgeTypes,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onInit,
    onDrop,
    onDragOver,
    isValidConnection,
    showMiniMap,
    setShowMiniMap,
    snapGrid,
    readonly,
    configureOnly,
}) => {
    const theme = useTheme();
    const nodesCanBeMovedByKeyboard = useStore(nodesCanBeMovedByKeyboardSelector);
    const { isTakingSchemeScreenshot } = useIsTakingSchemeScreenshot();
    const { t } = useTranslation();
    const { handleDeleteNode } = useDeleteNode();

    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            defaultViewport={defaultViewport}
            defaultEdgeOptions={defaultEdgeOptions}
            proOptions={proOptions}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={onInit}
            onDrop={onDrop}
            deleteKeyCode="Delete"
            onDragOver={onDragOver}
            fitView
            disableKeyboardA11y={!nodesCanBeMovedByKeyboard}
            nodesDraggable={!isTakingSchemeScreenshot && !readonly && !configureOnly}
            snapToGrid={true}
            snapGrid={snapGrid}
            nodesConnectable={!readonly}
            elementsSelectable={!readonly || configureOnly}
            className="scheme-drawer-flow"
            isValidConnection={isValidConnection}
            onNodesDelete={(deletedNodes) => deletedNodes.forEach(handleDeleteNode)}
        >
            {showMiniMap && (
                <MiniMap
                    position={Position.Right}
                    style={{ backgroundColor: theme.palette.components.navigation.background }}
                    nodeStrokeWidth={3}
                    zoomable
                    pannable
                />
            )}
            <Controls>
                <ControlButton
                    onClick={() => setShowMiniMap((prev) => !prev)}
                    title={`${showMiniMap ? t('hide') : t('show')} ${t('mini_map')}`}
                >
                    <MapIcon />
                </ControlButton>
            </Controls>
            <Background
                id="1"
                gap={10}
                lineWidth={0.5}
                color={theme.palette.mode === 'light' ? '#eeeeee' : '#383e59'}
                variant={BackgroundVariant.Lines}
            />
            <Background
                id="2"
                gap={100}
                offset={1}
                lineWidth={1}
                color={theme.palette.mode === 'light' ? '#ececec' : '#353a52'}
                variant={BackgroundVariant.Lines}
            />
        </ReactFlow>
    );
};

FlowInstance.propTypes = {
    nodes: PropTypes.array,
    edges: PropTypes.array,
    defaultViewport: PropTypes.object,
    defaultEdgeOptions: PropTypes.object,
    proOptions: PropTypes.object,
    nodeTypes: PropTypes.object,
    edgeTypes: PropTypes.object,
    onNodesChange: PropTypes.func,
    onEdgesChange: PropTypes.func,
    onConnect: PropTypes.func,
    onInit: PropTypes.func,
    onDrop: PropTypes.func,
    onDragOver: PropTypes.func,
    isValidConnection: PropTypes.func,
    showMiniMap: PropTypes.bool,
    setShowMiniMap: PropTypes.func,
};

export default FlowInstance;
